import { Chip, Divider, Icon, Skeleton } from "@mui/material";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utcPlugin from "dayjs/plugin/utc";
import { useContext, useMemo, useState } from "react";
import BitumioContext from "providers/BitumioContext";
import { useContactActivities } from "hooks/activities/useContactActivities";
import { useCompanyProposalsWithEvents } from "hooks/activities/useCompanyProposalsWithEvents";
import LogActivityModal from "modules/activities/LogActivityModal/LogActivityModal";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const ACTIVITY_PAGE_SIZE = 50;

const ACTIVITY_ICON_MAP: Record<string, string> = {
  CALL: "call",
  TEXT: "sms",
  EMAIL: "email",
  MEETING: "event",
  NOTE: "sticky_note_2",
  PROPOSAL_SENT: "send",
  PROPOSAL_VIEWED: "visibility",
  PROPOSAL_SIGNED: "draw",
  EMAIL_OPENED: "mark_email_read",
  EMAIL_CLICKED: "ads_click",
};

const ACTIVITY_LABEL_MAP: Record<string, string> = {
  CALL: "Call",
  TEXT: "Text Message",
  EMAIL: "Email",
  MEETING: "Meeting",
  NOTE: "Note",
  PROPOSAL_SENT: "Proposal sent",
  PROPOSAL_VIEWED: "Proposal viewed",
  PROPOSAL_SIGNED: "Proposal signed",
  EMAIL_OPENED: "Email opened",
  EMAIL_CLICKED: "Email clicked",
};


type UnifiedItem = {
  id: string;
  source: "USER" | "SYSTEM";
  type: string;
  summary: string;
  body?: string | null;
  occurredAt: string;
  proposalId?: string | null;
};

function iconForType(type: string) {
  const normalizedType = (type ?? "").toUpperCase();
  return ACTIVITY_ICON_MAP[normalizedType] ?? "bolt";
}

function labelForType(type: string) {
  const normalizedType = (type ?? "").toUpperCase();
  return ACTIVITY_LABEL_MAP[normalizedType] ?? "Activity";
}

function mapProposalEventActionToType(action: string) {
  const normalizedAction = (action ?? "").toLowerCase();
  const map: Record<string, string> = {
    sent: "PROPOSAL_SENT",
    resent: "PROPOSAL_SENT",
    viewed: "PROPOSAL_VIEWED",
    email_opened: "EMAIL_OPENED",
    email_clicked: "EMAIL_CLICKED",
  };
  return map[normalizedAction] ?? action?.toUpperCase?.() ?? "SYSTEM";
}

function formatOccurredAt(value: string, timezone: string) {
  return dayjs.utc(value).tz(timezone).format("MMM D, YYYY [at] h:mma")
}

export default function ContactActivityList({
  contactId,
  companyId,
}: {
  contactId?: string | null;
  companyId?: string | null;
}) {
  const { signedInUser } = useContext(BitumioContext);

  const timezone = useMemo(() => {
    return signedInUser.organization?.timezone ?? dayjs.tz.guess() ?? "America/Denver";
  }, [signedInUser]);

  const { activities: manualActivities, loading: manualLoading, error: manualError } = useContactActivities({
    contactId,
    first: ACTIVITY_PAGE_SIZE,
    page: 1,
  });

  const { proposals, loading: proposalsLoading, error: proposalsError } = useCompanyProposalsWithEvents({
    companyId,
    first: ACTIVITY_PAGE_SIZE,
    page: 1,
  });

  const systemItems: UnifiedItem[] = useMemo(() => {
    if (!contactId) return [];

    const proposalsForContact = proposals;

    const items: UnifiedItem[] = [];

    for (const proposal of proposalsForContact) {
      if (items.length >= ACTIVITY_PAGE_SIZE) break;

      if (proposal?.sentAt) {
        items.push({
          id: `proposal:${proposal.id}:sentAt`,
          source: "SYSTEM",
          type: "PROPOSAL_SENT",
          summary: "Proposal sent",
          occurredAt: proposal.sentAt,
          proposalId: proposal.id,
        });
      }

      if (items.length >= ACTIVITY_PAGE_SIZE) break;

      if (proposal?.signedAt) {
        items.push({
          id: `proposal:${proposal.id}:signedAt`,
          source: "SYSTEM",
          type: "PROPOSAL_SIGNED",
          summary: "Proposal signed",
          occurredAt: proposal.signedAt,
          proposalId: proposal.id,
        });
      }

      const events = proposal?.events ?? [];
      for (const event of events) {
        if (items.length >= ACTIVITY_PAGE_SIZE) break;

        const eventType = mapProposalEventActionToType(event.action);
        items.push({
          id: `proposalEvent:${event.id}`,
          source: "SYSTEM",
          type: eventType,
          summary: labelForType(eventType),
          occurredAt: event.createdAt,
          proposalId: proposal.id,
        });
      }
    }

    return items;
  }, [proposals, contactId]);

  const unified: UnifiedItem[] = useMemo(() => {
    const userItems: UnifiedItem[] = manualActivities.map((a) => ({
      id: `activity:${a.id}`,
      source: "USER",
      type: a.type,
      summary: a.summary,
      body: a.body ?? null,
      occurredAt: a.occurredAt,
    }));

    const all = [...systemItems, ...userItems];
    all.sort((a, b) => {
      const ad = new Date(a.occurredAt).getTime();
      const bd = new Date(b.occurredAt).getTime();
      if (bd !== ad) return bd - ad;
      return a.id.localeCompare(b.id);
    });
    return all.slice(0, ACTIVITY_PAGE_SIZE);
  }, [manualActivities, systemItems]);

  const [open, setOpen] = useState(false);

  const loading = manualLoading || proposalsLoading;
  const error = manualError || proposalsError;

  return (
    <MDBox width="100%">
      <MDBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between">
        <MDTypography variant="h6" fontWeight="medium">
          Activity
        </MDTypography>
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => setOpen(true)}
          disabled={!contactId}
          startIcon={<Icon>add</Icon>}
        >
          Log Activity
        </MDButton>
      </MDBox>

      <MDBox p={2}>
        <MDBox opacity={0.3}>
          <Divider />
        </MDBox>

        {!contactId ? (
          <MDBox mt={2}>
            <MDTypography variant="body2" color="text">
              No contact selected.
            </MDTypography>
          </MDBox>
        ) : error ? (
          <MDBox mt={2}>
            <MDAlert color="error">{error.message}</MDAlert>
          </MDBox>
        ) : (
          <MDBox
            mt={2}
            sx={{
              pr: 1,
            }}
          >
            {loading ? (
              <>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </>
            ) : unified.length === 0 ? (
              <MDTypography variant="body2" color="text">
                No activities yet.
              </MDTypography>
            ) : (
              unified.map((a) => (
                <MDBox
                  key={a.id}
                  sx={{
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small">{iconForType(a.type)}</Icon>
                    <MDTypography variant="body2" fontWeight="medium">
                      {labelForType(a.type)}: {a.summary}
                    </MDTypography>

                    <MDBox flex={1} />
                    <Chip
                      size="small"
                      label={a.source === "SYSTEM" ? "System" : "User"}
                      color={a.source === "SYSTEM" ? "info" : "success"}
                      sx={{
                        fontWeight: 500,
                        height: 20,
                      }}
                    />
                  </MDBox>

                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                    {formatOccurredAt(a.occurredAt, timezone)}
                  </MDTypography>

                  {a.body ? (
                    <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
                      {a.body}
                    </MDTypography>
                  ) : null}
                </MDBox>
              ))
            )}
          </MDBox>
        )}
      </MDBox>

      <LogActivityModal
        open={open}
        onClose={() => setOpen(false)}
        contactId={contactId}
        companyId={companyId}
        timezone={timezone}
      />
    </MDBox>
  );
}
